import reactUpdate from 'immutability-helper';
import React from 'react';
// @ts-expect-error
import PureRenderMixin from 'react-addons-pure-render-mixin';
import _ from 'underscore';

import {Protocol} from 'notebook/ELNConstants';
import Editor from 'notebook/mediocre/core-ui/Editor';
import {SplitEvent, TabEvent} from 'notebook/mediocre/EditorTypes';
import formatters from 'notebook/mediocre/formatted-text/formatters';
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import {SUPPORTED_TYPES, uploadFile} from 'notebook/protocols/editor/ImageEditable';
import STEPS_EDITOR_PLUGINS from 'notebook/protocols/editor/StepsEditorPlugins';
import {
  StepsEditor as StepsEditorInterface,
  StepsEditorProps,
  WrappedStepsEditorProps,
} from 'notebook/protocols/editor/StepsEditorTypes';
import ProtocolStoreTransforms from 'notebook/protocols/ProtocolStoreTransforms';
import {EditorKey, Step, StepLevel} from 'notebook/protocols/StepConstants';
import WithSpreadsheetEvaluationUserMixin from 'notebook/tables/spreadsheets/WithSpreadsheetEvaluationUserMixin';

interface StepsEditorState {
  highlightIndex?: number;
  insertIndex?: number;
}

interface StepMergeUpEvent {
  anchor?: boolean;
}

interface MoveHandlerEvent {
  up: boolean;
  beginning: boolean;
}

interface TabRangeParams {
  start: number;
  end: number;
  shiftKey: boolean;
}

interface StepsEditorClassInterface
  extends
    Omit<StepsEditorInterface, 'props'>,
    Partial<React.Component<WrappedStepsEditorProps, Partial<StepsEditorState>>> {
  defaultItemType: () => StepLevel;
  getSteps: () => Array<Step>;
  setSteps: (newSteps: Array<Step>) => void;
  stepMergeUpHandler: (index: number, e: StepMergeUpEvent) => void;
  formatHandler: (index: number, e: unknown) => void;
  updateTimeHandler: (index: number, time: number) => void;
  stepSplitHandler: (index: number, e: SplitEvent) => void;
  moveHandler: (index: number, e: MoveHandlerEvent) => void;
  tabHandler: (index: number, e: TabEvent) => void;
  tabRangeHandler: (params: TabRangeParams) => void;
  getFormats: (item: Step) => Step['formats'];
  insertItemHandler: (index: number, type?: StepLevel | null) => void;
}

const TABBABLE_LEVELS = [StepLevel.HEADER, StepLevel.STEP, StepLevel.DESC];

function levelTab(level: StepLevel): StepLevel {
  switch (level) {
    case StepLevel.HEADER:
      return StepLevel.STEP;
    case StepLevel.STEP:
      return StepLevel.DESC;
    default:
      return level;
  }
}

function levelUntab(level: StepLevel): StepLevel {
  switch (level) {
    case StepLevel.STEP:
      return StepLevel.HEADER;
    case StepLevel.DESC:
      return StepLevel.STEP;
    default:
      return level;
  }
}

const makeStep = ProtocolStoreTransforms.makeStep.bind(ProtocolStoreTransforms);
const makeTable = ProtocolStoreTransforms.makeTable.bind(ProtocolStoreTransforms);
const makeImage = ProtocolStoreTransforms.makeImage.bind(ProtocolStoreTransforms);
const makeTimer = ProtocolStoreTransforms.makeTimer.bind(ProtocolStoreTransforms);

const StepsEditor = Editor.createClass<WrappedStepsEditorProps, StepsEditorState, StepsEditorClassInterface>({
  mixins: [WithSpreadsheetEvaluationUserMixin, PureRenderMixin],

  plugins: STEPS_EDITOR_PLUGINS,
  displayName: 'StepsEditor',

  UNSAFE_componentWillReceiveProps(nextProps: StepsEditorProps) {
    this.setState?.({highlightIndex: nextProps.highlightIndex, insertIndex: nextProps.insertIndex});
  },

  _getRoot() {
    return this.props!.rootRef.current;
  },

  getComputedTables(root: Protocol) {
    return null;
  },

  getValidationErrorSnapshot() {
    return {};
  },

  isVirtualizationSupported() {
    return false;
  },

  // Required by Editor
  editorName() {
    return 'steps';
  },
  getDocumentVersionCid() {
    return this.props!.protocol.cid;
  },
  multiSelectKey() {
    return 'steps';
  },
  defaultInsertType: StepLevel.HEADER,
  textType: StepLevel.STEP,
  tableType: StepLevel.TABLE,
  headerType: StepLevel.HEADER,
  descType: StepLevel.DESC,
  getExtraTypingPerfMetadata() {
    const items = this.props!.getItems(this.props!.getRoot());
    const itemCounts = _.countBy(items, (item) => `num_${item.level.toLowerCase()}`);
    // eslint-disable-next-line no-restricted-syntax
    return _.extend(
      {
        num_items: items.length,
      },
      itemCounts
    );
  },
  makeEditorProps(props: StepsEditorProps) {
    // eslint-disable-next-line no-restricted-syntax
    return _.extend(this.makeEditorPropsBase(props), {
      spellCheck: props.spellCheck,
    });
  },

  isLockedWithEditWarn() {
    return this.props!.isLockedWithEditWarn();
  },

  // Steps don't have a headerType property; instead they use {type: StepLevel.HEADER}
  getHeaderType(item: Step) {
    return null;
  },
  canItemHaveHeader(item: Step) {
    return false;
  },
  headerTypeAccessor() {
    throw new Error('Unexpected call to headerTypeAccessor.');
  },
  getFormats(item: Step) {
    return item.formats;
  },
  makeItem(level: StepLevel) {
    switch (level) {
      case StepLevel.HEADER:
      case StepLevel.STEP:
      case StepLevel.DESC:
        return makeStep('', formatters.defaultFormats(), level);
      case StepLevel.TIMER:
        return makeTimer();
      case StepLevel.IMAGE:
        return makeImage();
      case StepLevel.TABLE:
        return makeTable(null, null, 2, 2); // name will be properly set by caller.
      default:
        throw new Error(`Unexpected level: ${level}`);
    }
  },

  replaceItems() {
    // oxlint-disable-next-line prefer-rest-params
    this.replaceItemsDefault(...arguments);
  },
  replaceItemsForAtomicMove() {
    // oxlint-disable-next-line prefer-rest-params
    this.replaceItemsForAtomicMoveDefault(...arguments);
  },
  deleteRangeHandler() {
    // oxlint-disable-next-line prefer-rest-params
    return this.deleteRangeHandlerDefault(...arguments);
  },
  isItemTextual(item: Step) {
    return this.getPluginByItem(item).textual;
  },
  itemHasText(item: Step) {
    return this.isItemTextual(item);
  },
  insertItemHandler(index: number, type: StepLevel | null = null) {
    if (type == null) {
      type = this.defaultItemType();
    }
    this.insertDefaultableItemHandler(index, type);
  },

  parseItemsFromFile(file: File) {
    if (!Array.from(SUPPORTED_TYPES).includes(file.type)) {
      return Promise.resolve({});
    }
    const deferred = uploadFile(file).then((url: string) => {
      const imageItem = reactUpdate(makeImage(), {
        image: {$merge: {url}},
      });
      return {items: [imageItem]};
    });
    return Promise.resolve(deferred);
  },

  render() {
    return (
      <div className="stepsEditor mediocre" ref={this.props!.rootRef}>
        {this.shouldRenderMaximizedTable() ? this.renderFocusedTable() : this.renderEditor()}
      </div>
    );
  },

  defaultItemType() {
    return StepLevel.STEP;
  },

  // Convenience methods
  getSteps() {
    return this.props!.getItems(this.props!.getRoot());
  },

  setSteps(newSteps: Array<Step>) {
    const newProtocol = reactUpdate(this.props!.getRoot(), this.props!.itemsAccessor({$set: newSteps}));
    this.props!.updateRoot(newProtocol);
  },

  // Required by textual plugins
  stepMergeUpHandler(index: number, e: StepMergeUpEvent) {
    let newStep;
    let newSteps;
    const {anchor} = e;
    const steps = this.getSteps();

    const mergeSteps = (index: number): void => {
      let text;
      const aboveStep = steps[index];
      const belowStep = steps[index + 1];
      if (
        Array.from(TABBABLE_LEVELS).includes(aboveStep.level) &&
        Array.from(TABBABLE_LEVELS).includes(belowStep.level)
      ) {
        // Normal merge betwen two textual steps.
        const aboveFmtText = this.getText(aboveStep);
        const belowFmtText = this.getText(belowStep);
        const cursor = aboveFmtText.text.length;
        text = aboveFmtText.text + belowFmtText.text;
        const formats = formatters.joinFormats(aboveFmtText.formats, belowFmtText.formats, cursor);
        newStep = this.withUpdatedText(aboveStep, {text, formats});
        newSteps = reactUpdate(steps, {
          $splice: [
            [index, 1, newStep],
            [index + 1, 1],
          ],
        });
        this.setSteps(newSteps);
        this.props!.requestSelection({selection: {cid: newStep.cid, start: cursor, end: cursor}});
      } else if (Array.from(TABBABLE_LEVELS).includes(aboveStep.level) && !aboveStep.text?.length) {
        // Empty aboveStep, belowStep is non-text so selection goes to the beginning of belowStep.
        newSteps = reactUpdate(steps, {$splice: [[index, 1]]});
        this.setSteps(newSteps);
        this.props!.requestSelection(this.selectionAtStart(belowStep));
      } else if (Array.from(TABBABLE_LEVELS).includes(belowStep.level) && !belowStep.text?.length) {
        // Empty belowStep, aboveStep is non-text so selection goes to the end of aboveStep
        newSteps = reactUpdate(steps, {$splice: [[index + 1, 1]]});
        this.setSteps(newSteps);
        this.props!.requestSelection(this.selectionAtEnd(aboveStep));
      }
    };

    if (anchor) {
      // Merge with below, if possible.
      if (index + 1 < steps.length) {
        mergeSteps(index);
      }
    } else {
      // Merge with above, if possible.
      // oxlint-disable-next-line no-lonely-if
      if (index - 1 >= 0) {
        mergeSteps(index - 1);
      } else {
        // Otherwise, untab.
        newStep = reactUpdate(steps[0], {$merge: {level: levelUntab(steps[0].level)}});
        newSteps = reactUpdate(steps, {$splice: [[0, 1, newStep]]});
        this.setSteps(newSteps);
        this.props!.requestSelection({selection: {cid: steps[0].cid, start: 0, end: 0}});
      }
    }
  },

  formatHandler(index: number, e: unknown) {
    this.props!.formatHandler(index, e);
  },

  updateTimeHandler(index: number, time: number) {
    const steps = this.getSteps();
    const newStep = reactUpdate(steps[index], {time_seconds: {$set: time}});
    const newSteps = reactUpdate(steps, {$splice: [[index, 1, newStep]]});
    this.setSteps(newSteps);
  },

  stepSplitHandler(index: number, e: SplitEvent) {
    let levels;
    const {before, selected, after} = e;
    const steps = this.getSteps();
    const step = steps[index];

    let {formats} = this.getText(step);
    const beforeFormats = formatters.splitFormats(formats, before.length).before;
    const afterFormats = formatters.splitFormats(formats, selected.length + before.length).after;
    let parts = [before, after]; // enter replaces middle selection.
    let cursor = {index: 1, offset: 0};

    formats = [beforeFormats, afterFormats];
    if (step.level === StepLevel.HEADER) {
      levels = [StepLevel.HEADER, StepLevel.STEP];
    } else if (e.shiftKey) {
      levels =
        step.level === StepLevel.STEP ? [StepLevel.STEP, StepLevel.DESC] : [StepLevel.DESC, StepLevel.STEP]; // enter/exit desc mode.
    } else if (parts[0].length === parts[1].length && parts[1].length === 0) {
      levels = [levelUntab(step.level)]; // if empty, go up a level.
      formats = [formatters.defaultFormats()];
      parts = [''];
      cursor = {index: 0, offset: 0};
    } else {
      levels = [step.level, step.level]; // normal split
    }

    const firstLevel = levels.shift();
    const firstText = parts[0];
    const firstFormat = formats.shift();
    const restTexts = parts.slice(1);

    const newStep = reactUpdate(step, {
      $merge: {
        text: firstText,
        formats: firstFormat,
        level: firstLevel,
      },
    });

    const restSteps = (() => {
      const result = [];
      for (const [stepText, stepLevel, stepFormat] of Array.from(_.zip(restTexts, levels, formats))) {
        result.push(makeStep(stepText, stepFormat, stepLevel));
      }
      return result;
    })();

    const newSteps = reactUpdate(steps, {$splice: [[index, 1, newStep, ...Array.from(restSteps)]]});

    this.setSteps(newSteps);
    this.setState?.({insertIndex: cursor.index + index});
    const newCid = newSteps[cursor.index + index].cid;
    this.props!.requestSelection({selection: {cid: newCid, start: cursor.offset, end: cursor.offset}});
  },

  moveHandler(index: number, e: MoveHandlerEvent) {
    const {up, beginning} = e;

    const steps = this.getSteps();
    const step = steps[index];

    const dir = up ? -1 : 1;
    const targetStep = steps[index + dir];

    if (targetStep != null) {
      if (beginning) {
        this.props!.requestSelection(this.selectionAtStart(targetStep));
      } else {
        this.props!.requestSelection(this.selectionAtEnd(targetStep));
      }
    } else if (up) {
      this.clearSelection();
      const location = beginning ? 'start' : 'end';
      this.props!.jumpTo({section: EditorKey.MATERIALS, location});
    } else if (this.props!.getType(step) === StepLevel.TABLE && !this.props!.locked) {
      this.insertItemHandler(index, StepLevel.STEP);
    }
  },

  tabHandler(index: number, e: TabEvent) {
    const {shiftKey} = e;

    const steps = this.getSteps();
    const step = steps[index];

    if (!Array.from(TABBABLE_LEVELS).includes(step.level)) {
      return;
    }

    const newLevel = shiftKey ? levelUntab(step.level) : levelTab(step.level);
    const newStep = reactUpdate(step, {$merge: {level: newLevel}});

    const newSteps = reactUpdate(steps, {$splice: [[index, 1, newStep]]});
    this.setSteps(newSteps);
    this.props!.requestSelection({selection: selutil.getSelectionIndices(e)!});
  },

  tabRangeHandler({start, end, shiftKey}: TabRangeParams) {
    let step;
    const steps = this.getSteps();

    const insert = shiftKey
      ? (() => {
          const result = [];
          for (step of Array.from(steps.slice(start, Number(end) + 1 || undefined))) {
            if (Array.from(TABBABLE_LEVELS).includes(step.level)) {
              result.push(reactUpdate(step, {$merge: {level: levelUntab(step.level)}}));
            } else {
              result.push(step);
            }
          }
          return result;
        })()
      : (() => {
          const result1 = [];
          for (step of Array.from(steps.slice(start, Number(end) + 1 || undefined))) {
            if (Array.from(TABBABLE_LEVELS).includes(step.level)) {
              result1.push(reactUpdate(step, {$merge: {level: levelTab(step.level)}}));
            } else {
              result1.push(step);
            }
          }
          return result1;
        })();
    const newSteps = reactUpdate(steps, {$splice: [[start, end - start + 1, ...Array.from(insert)]]});
    this.setSteps(newSteps);
  },

  openManageSelectorsModal(id?: string) {
    this.props!.openManageSelectorsModal(id);
  },
});

export default StepsEditor;
